import { deferred } from './components/ui/deferred';
const CongregationPage = deferred(
  async () => ({ default: (await import('./pages/CongregationPage')).CongregationPage }),
  'nav.congregation'
);
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Layout } from './components/layout/Layout';
import { LoginPage } from './pages/LoginPage';
const DashboardPage = deferred(
  async () => ({ default: (await import('./pages/DashboardPage')).DashboardPage }),
  'nav.dashboard'
);
const FieldServicePage = deferred(
  async () => ({ default: (await import('./pages/FieldServicePage')).FieldServicePage }),
  'nav.fieldService'
);
const ServicePlanningPage = deferred(
  async () => ({ default: (await import('./pages/ServicePlanningPage')).ServicePlanningPage }),
  'nav.servicePlanning'
);
const MidweekMeetingPlanningPage = deferred(
  async () => ({
    default: (await import('./pages/MidweekMeetingPlanningPage')).MidweekMeetingPlanningPage,
  }),
  'nav.midweekMeetingPlanning'
);
const PlanCyclesPage = deferred(
  async () => ({ default: (await import('./pages/PlanCyclesPage')).PlanCyclesPage }),
  'nav.planCycles'
);
const AbsencesPage = deferred(
  async () => ({ default: (await import('./pages/AbsencesPage')).AbsencesPage }),
  'nav.absences'
);
const DeepLinkPage = deferred(async () => ({
  default: (await import('./pages/DeepLinkPage')).DeepLinkPage,
}));
import { NotFoundPage } from './pages/NotFoundPage';
const UserManagementPage = deferred(
  async () => ({ default: (await import('./pages/UserManagementPage')).UserManagementPage }),
  'nav.userManagement'
);
import { PERMISSIONS, PLAN_CYCLE_APP_ROLES } from './lib/permissions';

export function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/publisher" element={<DeepLinkPage />} />
        <Route path="/publisher-access" element={<DeepLinkPage />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route
            path="congregation-settings"
            element={
              <ProtectedRoute requiredAppRole="admin">
                <Navigate to="/congregation?section=settings" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="congregation"
            element={
              <ProtectedRoute requiredAppRole={['admin', 'lamm_overseer']}>
                <CongregationPage />
              </ProtectedRoute>
            }
          />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route
            path="plan-cycles"
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.lammRead, PERMISSIONS.servicesRead]}
                requiredAppRole={PLAN_CYCLE_APP_ROLES}
              >
                <PlanCyclesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="service-planning"
            element={
              <ProtectedRoute requiredPermissions={[PERMISSIONS.servicesRead]}>
                <ServicePlanningPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="midweek-meeting-planning"
            element={
              <ProtectedRoute requiredPermissions={[PERMISSIONS.lammRead]}>
                <MidweekMeetingPlanningPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="publishers"
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.publishersWrite]}
                requiredAppRole={['admin', 'lamm_overseer']}
              >
                <Navigate to="/congregation?section=publishers" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="field-service"
            element={
              <ProtectedRoute requiredPermissions={[PERMISSIONS.fieldServiceRead]}>
                <FieldServicePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="absences"
            element={
              <ProtectedRoute requiredPermissions={[PERMISSIONS.absencesRead]}>
                <AbsencesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="users"
            element={
              <ProtectedRoute
                requiredPermissions={[PERMISSIONS.usersRead, PERMISSIONS.permissionsManage]}
                requiredAppRole="admin"
              >
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* 404 route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
